import React, { useState, useEffect } from 'react';
import { Mail } from 'react-feather';
import PageHeader from 'shared/components/PageHeader/PageHeader';
import { getAllContactRequests, updateContactRequestStatus } from "shared/utils/firebase";
import styles from './ContactRequests.module.css';

const ContactRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const categories = [
        'General Inquiry',
        'Technical Support',
        'Billing & Payments',
        'Course Information',
        'Enterprise Solutions',
        'Partnership Opportunities',
        'Feedback & Suggestions',
        'Report an Issue',
        'Other'
    ];

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const snapshot = await getAllContactRequests();
            if (snapshot) {
                const requestsData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt?.toDate()
                }));
                setRequests(requestsData);
            }
        } catch (error) {
            console.error('Error fetching contact requests:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (requestId, newStatus) => {
        try {
            await updateContactRequestStatus(requestId, newStatus);
            setRequests(requests.map(req =>
                req.id === requestId ? { ...req, status: newStatus, read: true } : req
            ));
            if (selectedRequest?.id === requestId) {
                setSelectedRequest({ ...selectedRequest, status: newStatus, read: true });
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleViewRequest = async (request) => {
        setSelectedRequest(request);
        if (!request.read) {
            await updateContactRequestStatus(request.id, request.status, true);
            setRequests(requests.map(req =>
                req.id === request.id ? { ...req, read: true } : req
            ));
        }
    };

    const getFilteredRequests = () => {
        return requests.filter(req => {
            const matchesStatus = filterStatus === 'all' || req.status === filterStatus;
            const matchesCategory = filterCategory === 'all' || req.category === filterCategory;
            const matchesSearch = !searchTerm ||
                req.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                req.subject?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesStatus && matchesCategory && matchesSearch;
        });
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'new': return styles.badgeNew;
            case 'in-progress': return styles.badgeInProgress;
            case 'resolved': return styles.badgeResolved;
            case 'closed': return styles.badgeClosed;
            default: return styles.badgeNew;
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const filteredRequests = getFilteredRequests();
    const stats = {
        total: requests.length,
        new: requests.filter(r => r.status === 'new').length,
        inProgress: requests.filter(r => r.status === 'in-progress').length,
        resolved: requests.filter(r => r.status === 'resolved').length
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <i className="fas fa-spinner fa-spin fa-3x"></i>
                <p>Loading contact requests...</p>
            </div>
        );
    }

    return (
        <div className={styles.contactRequestsPage}>
            <PageHeader
                icon={Mail}
                title="Student & Visitor Contact Requests"
                subtitle="Review and process incoming contact inquiries, feedback, and support tickets"
                badge={`${stats.total} Requests`}
                actions={
                    <button className={styles.btnRefresh} onClick={fetchRequests}>
                        <i className="fas fa-sync-alt me-2"></i> Refresh
                    </button>
                }
            />

            {/* Stats Cards */}
            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon} style={{ background: '#dbeafe' }}>
                        <i className="fas fa-envelope" style={{ color: '#2563eb' }}></i>
                    </div>
                    <div className={styles.statInfo}>
                        <h3>{stats.total}</h3>
                        <p>Total Requests</p>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon} style={{ background: '#fef3c7' }}>
                        <i className="fas fa-bell" style={{ color: '#f59e0b' }}></i>
                    </div>
                    <div className={styles.statInfo}>
                        <h3>{stats.new}</h3>
                        <p>New</p>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon} style={{ background: '#ddd6fe' }}>
                        <i className="fas fa-hourglass-half" style={{ color: '#8b5cf6' }}></i>
                    </div>
                    <div className={styles.statInfo}>
                        <h3>{stats.inProgress}</h3>
                        <p>In Progress</p>
                    </div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statIcon} style={{ background: '#d1fae5' }}>
                        <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                    </div>
                    <div className={styles.statInfo}>
                        <h3>{stats.resolved}</h3>
                        <p>Resolved</p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className={styles.filtersSection}>
                <div className={styles.searchBox}>
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search by name, email, or subject..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <select
                    className={styles.filterSelect}
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                >
                    <option value="all">All Status</option>
                    <option value="new">New</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                </select>
                <select
                    className={styles.filterSelect}
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
            </div>

            {/* Requests List and Detail View */}
            <div className={styles.contentSection}>
                <div className={styles.requestsList}>
                    <h3>Requests ({filteredRequests.length})</h3>
                    {filteredRequests.length === 0 ? (
                        <div className={styles.emptyState}>
                            <i className="fas fa-inbox fa-3x"></i>
                            <p>No requests found</p>
                        </div>
                    ) : (
                        <div className={styles.requestItems}>
                            {filteredRequests.map(request => (
                                <div
                                    key={request.id}
                                    className={`${styles.requestItem} ${!request.read ? styles.unread : ''} ${selectedRequest?.id === request.id ? styles.active : ''}`}
                                    onClick={() => handleViewRequest(request)}
                                >
                                    <div className={styles.requestHeader}>
                                        <h4>{request.name}</h4>
                                        <span className={getStatusBadgeClass(request.status)}>
                                            {request.status || 'new'}
                                        </span>
                                    </div>
                                    <p className={styles.requestSubject}>{request.subject}</p>
                                    <div className={styles.requestMeta}>
                                        <span className={styles.category}>
                                            <i className="fas fa-tag"></i> {request.category}
                                        </span>
                                        <span className={styles.date}>
                                            <i className="fas fa-clock"></i> {formatDate(request.createdAt)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.requestDetail}>
                    {selectedRequest ? (
                        <>
                            <div className={styles.detailHeader}>
                                <h3>Request Details</h3>
                                <button
                                    className={styles.btnClose}
                                    onClick={() => setSelectedRequest(null)}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className={styles.detailContent}>
                                <div className={styles.detailSection}>
                                    <label>Status</label>
                                    <select
                                        className={styles.statusSelect}
                                        value={selectedRequest.status || 'new'}
                                        onChange={(e) => handleStatusUpdate(selectedRequest.id, e.target.value)}
                                    >
                                        <option value="new">New</option>
                                        <option value="in-progress">In Progress</option>
                                        <option value="resolved">Resolved</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>

                                <div className={styles.detailSection}>
                                    <label>Name</label>
                                    <p>{selectedRequest.name}</p>
                                </div>

                                <div className={styles.detailSection}>
                                    <label>Email</label>
                                    <p>
                                        <a href={`mailto:${selectedRequest.email}`}>
                                            {selectedRequest.email}
                                        </a>
                                    </p>
                                </div>

                                {selectedRequest.phone && (
                                    <div className={styles.detailSection}>
                                        <label>Phone</label>
                                        <p>
                                            <a href={`tel:${selectedRequest.phone}`}>
                                                {selectedRequest.phone}
                                            </a>
                                        </p>
                                    </div>
                                )}

                                <div className={styles.detailSection}>
                                    <label>Category</label>
                                    <p>{selectedRequest.category}</p>
                                </div>

                                <div className={styles.detailSection}>
                                    <label>Subject</label>
                                    <p>{selectedRequest.subject}</p>
                                </div>

                                <div className={styles.detailSection}>
                                    <label>Message</label>
                                    <div className={styles.messageBox}>
                                        {selectedRequest.message}
                                    </div>
                                </div>

                                <div className={styles.detailSection}>
                                    <label>Received On</label>
                                    <p>{formatDate(selectedRequest.createdAt)}</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={styles.emptyDetail}>
                            <i className="fas fa-mouse-pointer fa-3x"></i>
                            <p>Select a request to view details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactRequests;
